import { EventEmitterUtil } from '@chesscom/web-ui';
import { Plugins } from 'utils/plugins/config/types';

const { createEventEmitter } = EventEmitterUtil;

/**
 * Creates a generic API for managing plugins.
 */
export function createPluginsAPI<T, U = any>(
  initialPlugins: Plugins.Model<T, U>[] = [],
  initialContext?: U,
  emitter: EventEmitterUtil.EventEmitter = createEventEmitter(),
): Plugins.API<T, U> {
  const plugins = new Map<string, Plugins.Model<T, U>>();
  let context = initialContext;

  addMany(initialPlugins);

  return {
    add: addPlugin,
    addMany,
    destroy,
    ...emitter,
    getAll,
    getContext,
    getOne,
    has: plugins.has.bind(plugins),
    remove,
    setContext,
  };

  /**
   * Load a plugin.
   */
  function addPlugin(plugin: Plugins.Model<T, U>): boolean {
    if (!plugin.name) {
      return false;
    }

    if (plugins.has(plugin.name)) {
      remove(plugin.name);
    }

    plugins.set(plugin.name, plugin);
    plugin.create?.(context);

    emitter.emit(Plugins.Events.PluginAdded, plugin);

    return true;
  }

  /**
   * Add a plugin(s).
   */
  function addMany(newPlugins: Plugins.Model<T, U> | Plugins.Model<T, U>[]): boolean {
    if (!newPlugins || (<Plugins.Model<T, U>[]>newPlugins).length === 0) {
      return false;
    }

    const pluginsToAdd = Array.isArray(newPlugins) ? newPlugins : [newPlugins];

    return pluginsToAdd.reduce<boolean>(function pluginsAddedSuccessfully(acc, cur) {
      const added = addPlugin(cur);

      if (!added) {
        // eslint-disable-next-line no-param-reassign
        acc = false;
      }

      return acc;
    }, true);
  }

  /**
   * Destroy the plugins API.
   */
  function destroy() {
    plugins.forEach((plugin) => remove(plugin.name));
  }

  /**
   * Get the current plugins.
   */
  function getAll(): Plugins.Model<T, U>[] {
    return [...plugins.values()];
  }

  /**
   * Get the current context.
   */
  function getContext(): U | undefined {
    return context;
  }

  /**
   * Get the current plugins.
   */
  function getOne(key: string): Plugins.Model<T, U> | undefined {
    return plugins.get(key);
  }

  /**
   * Remove a plugin.
   */
  function remove(key: string): boolean {
    const plugin = plugins.get(key);

    if (!plugin) {
      return false;
    }

    plugin.destroy?.(context);
    plugins.delete(key);

    emitter.emit(Plugins.Events.PluginRemoved, plugin);

    return true;
  }

  /**
   * Set the plugin context.
   */
  function setContext(newContext: U): void {
    context = newContext;

    emitter.emit(Plugins.Events.ContextSet, context);
  }
}
